import React from 'react';

/**
 * Leaderboard component showing ranked participants
 */
export function RivalryLeaderboard({ leaderboard, currentUserId, className = '' }) {
  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className={`bg-gray-800 rounded-lg p-6 ${className}`}>
        <h2 className="text-xl font-bold text-white mb-4">Leaderboard</h2>
        <p className="text-gray-400 text-center">No participants yet</p>
      </div>
    );
  }

  // Sort by rank
  const sortedLeaderboard = [...leaderboard].sort((a, b) => a.current_rank - b.current_rank);

  return (
    <div className={`bg-gray-800 rounded-lg ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-700">
        <h2 className="text-xl font-bold text-white">Leaderboard</h2>
        <p className="text-gray-400 text-sm mt-1">{leaderboard.length} fighters competing</p>
      </div>

      {/* Podium (Top 3) */}
      {sortedLeaderboard.length >= 3 && (
        <div className="p-6 border-b border-gray-700">
          <div className="grid grid-cols-3 gap-4">
            {/* 2nd Place */}
            <PodiumCard
              participant={sortedLeaderboard[1]}
              rank={2}
              isCurrentUser={sortedLeaderboard[1].user_id === currentUserId}
            />

            {/* 1st Place */}
            <PodiumCard
              participant={sortedLeaderboard[0]}
              rank={1}
              isCurrentUser={sortedLeaderboard[0].user_id === currentUserId}
              isWinner
            />

            {/* 3rd Place */}
            <PodiumCard
              participant={sortedLeaderboard[2]}
              rank={3}
              isCurrentUser={sortedLeaderboard[2].user_id === currentUserId}
            />
          </div>
        </div>
      )}

      {/* Full Leaderboard */}
      <div className="divide-y divide-gray-700">
        {sortedLeaderboard.map((participant, index) => (
          <LeaderboardRow
            key={participant.user_id}
            participant={participant}
            isCurrentUser={participant.user_id === currentUserId}
            showBorder={index > 0}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Podium card for top 3 finishers
 */
function PodiumCard({ participant, rank, isCurrentUser, isWinner }) {
  const { display_name, monthly_points } = participant;

  const colors = {
    1: { bg: 'bg-yellow-500', text: 'text-yellow-500', medal: '🥇' },
    2: { bg: 'bg-gray-400', text: 'text-gray-400', medal: '🥈' },
    3: { bg: 'bg-orange-600', text: 'text-orange-600', medal: '🥉' },
  };

  const color = colors[rank];
  const heightClass = isWinner ? 'h-32' : 'h-24';

  return (
    <div className={`flex flex-col items-center ${rank === 1 ? 'order-2' : rank === 2 ? 'order-1' : 'order-3'}`}>
      {/* Profile Image */}
      <div className={`w-16 h-16 rounded-full ${color.bg} flex items-center justify-center mb-2 ${isCurrentUser ? 'ring-4 ring-blue-500' : ''}`}>
        <span className="text-2xl">{color.medal}</span>
      </div>

      {/* Name */}
      <p className={`text-white font-semibold text-center text-sm mb-1 ${isCurrentUser ? 'text-blue-400' : ''}`}>
        {display_name || 'Fighter'}
      </p>

      {/* Points */}
      <p className={`${color.text} font-bold text-lg`}>{monthly_points}</p>
      <p className="text-gray-400 text-xs">points</p>

      {/* Podium */}
      <div className={`${color.bg} w-full ${heightClass} rounded-t-lg mt-2 flex items-end justify-center pb-2`}>
        <span className="text-white font-bold text-xl">#{rank}</span>
      </div>
    </div>
  );
}

/**
 * Individual leaderboard row
 */
function LeaderboardRow({ participant, isCurrentUser, showBorder }) {
  const { current_rank, display_name, monthly_points, workouts_completed, current_streak, rank_change } = participant;

  // Determine rank color
  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-400';
    if (rank === 2) return 'text-gray-300';
    if (rank === 3) return 'text-orange-400';
    return 'text-gray-500';
  };

  // Determine rank change indicator
  const getRankChangeIndicator = (change) => {
    if (change > 0) {
      return (
        <span className="text-green-500 text-sm flex items-center">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          {change}
        </span>
      );
    } else if (change < 0) {
      return (
        <span className="text-red-500 text-sm flex items-center">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
          {Math.abs(change)}
        </span>
      );
    }
    return <span className="text-gray-500 text-sm">-</span>;
  };

  return (
    <div
      className={`p-4 hover:bg-gray-750 transition-colors ${isCurrentUser ? 'bg-blue-900 bg-opacity-20 border-l-4 border-blue-500' : ''}`}
    >
      <div className="flex items-center">
        {/* Rank */}
        <div className="flex-shrink-0 w-12">
          <span className={`text-2xl font-bold ${getRankColor(current_rank)}`}>#{current_rank}</span>
        </div>

        {/* Change Indicator */}
        <div className="flex-shrink-0 w-10">{getRankChangeIndicator(rank_change)}</div>

        {/* Name */}
        <div className="flex-grow ml-4">
          <p className={`font-semibold ${isCurrentUser ? 'text-blue-400' : 'text-white'}`}>{display_name || 'Fighter'}</p>
          <p className="text-gray-400 text-xs">{workouts_completed} workouts</p>
        </div>

        {/* Streak */}
        {current_streak > 0 && (
          <div className="flex-shrink-0 mr-4">
            <div className="flex items-center text-orange-500">
              <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-semibold">{current_streak}</span>
            </div>
            <p className="text-gray-500 text-xs text-right">day streak</p>
          </div>
        )}

        {/* Points */}
        <div className="flex-shrink-0 text-right min-w-[80px]">
          <p className="text-2xl font-bold text-white">{monthly_points}</p>
          <p className="text-xs text-gray-400">points</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact leaderboard view (for smaller spaces)
 */
export function CompactLeaderboard({ leaderboard, currentUserId, maxRows = 5, className = '' }) {
  if (!leaderboard || leaderboard.length === 0) {
    return (
      <div className={`bg-gray-800 rounded-lg p-4 ${className}`}>
        <p className="text-gray-400 text-center text-sm">No participants yet</p>
      </div>
    );
  }

  const sortedLeaderboard = [...leaderboard].sort((a, b) => a.current_rank - b.current_rank).slice(0, maxRows);

  return (
    <div className={`bg-gray-800 rounded-lg ${className}`}>
      <div className="divide-y divide-gray-700">
        {sortedLeaderboard.map((participant) => (
          <div
            key={participant.user_id}
            className={`p-3 flex items-center justify-between ${participant.user_id === currentUserId ? 'bg-blue-900 bg-opacity-20' : ''}`}
          >
            <div className="flex items-center">
              <span className={`text-lg font-bold w-8 ${participant.current_rank <= 3 ? 'text-yellow-400' : 'text-gray-500'}`}>
                #{participant.current_rank}
              </span>
              <span className={`ml-3 ${participant.user_id === currentUserId ? 'text-blue-400 font-semibold' : 'text-white'}`}>
                {participant.display_name || 'Fighter'}
              </span>
            </div>
            <span className="text-white font-bold">{participant.monthly_points}</span>
          </div>
        ))}
      </div>

      {leaderboard.length > maxRows && (
        <div className="p-3 text-center border-t border-gray-700">
          <p className="text-gray-400 text-sm">+{leaderboard.length - maxRows} more fighters</p>
        </div>
      )}
    </div>
  );
}
